import {
	type NavigationTypeType,
	controlCharsObject,
	strings,
} from "@quickpoint.me/shared";
import {
	type ReactElement,
	useContext,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import {
	PageElementsFamily,
	PageIndex,
	PageSizeAtom,
	createLocation,
} from "../../state/page";
import { ConstrainDimensionsSelector, IsInPopOut } from "../../state/ui";
import {
	PositionContext,
	type PositionContextType,
} from "../displayElements/DisplayElements";

import { createPortal } from "react-dom";
import { useRecoilValue } from "recoil";
import styled from "styled-components";
import type { BackgroundProps } from "../../@types/viewerati";
import type { ElementProps } from "../../functionalComponents/RenderPage";
import useBackgroundAnimation from "../../lib/animations/backgroundAnimationsHook";
import modifierStyled from "../../lib/modifierStyled";
import { FilterColors } from "../../state/filter";
import { NavigationType } from "../../state/navigation";
import { IsInFlexBoxContext } from "../displayElements/Cell";
import { SplitPageContext } from "../displayElements/RenderSplitPage";
import BackgroundGridCell from "./BackgroundGridCell";

function getPosition(
	isInAuthor: boolean | undefined,
	isInPopOut: boolean,
	isInCell: boolean,
) {
	if (isInCell && isInAuthor && !isInPopOut) {
		return isInAuthor ? "absolute" : "fixed";
	}

	if (isInAuthor && !isInPopOut) {
		return "unset";
	}

	return isInAuthor ? "absolute" : "fixed";
}

function getDisplay(
	isInAuthor: boolean | undefined,
	isInPopOut: boolean,
	isInCell: boolean,
) {
	if (isInCell && isInAuthor && !isInPopOut) {
		return "grid";
	}
	if (isInAuthor && !isInPopOut) {
		return "none";
	}

	return "grid";
}

const StyledBackground = styled.div<BackgroundProps>`
  display:  ${(props) => {
		return getDisplay(
			props.$constrainDimensions,
			props.$isInPopOut,
			props.$isInCell,
		);
	}};
  visibility: ${(props) => (props.$tapped ? "visible" : "hidden")};
  grid-auto-flow: ${(props) => (props.$pageSize.landscape ? "column" : "row")};
  min-width: 100%;
  min-height: 100vh;
  position: ${(props) => {
		return getPosition(
			props.$constrainDimensions,
			props.$isInPopOut,
			props.$isInCell,
		);
	}};
  top: 0;
  left: 0;
  ${(props) => `${modifierStyled("background:", props)}`}
  margin-block-start: 0 !important;
  pointer-events: none;
`;

function getPortalId(
	position: PositionContextType,
	navigationType: NavigationTypeType,
) {
	if (navigationType === "tap") {
		return position === "left"
			? strings.navigation.idStoryBackgroundLeft
			: strings.navigation.idStoryBackgroundRight;
	}
	return position === "top"
		? strings.navigation.idStoryBackgroundTop
		: strings.navigation.idStoryBackgroundBottom;
}

const Background = ({
	atomId,
	pageIndex,
}: ElementProps): ReactElement<ElementProps> | null => {
	const globalPageIndex = useRecoilValue(PageIndex);
	const navigationType = useRecoilValue(NavigationType);

	const backGroundRef = useRef<HTMLDivElement>(null);
	const pageSize = useRecoilValue(PageSizeAtom);
	const [isInCell, setIsInCell] = useState(false);
	const baseFontSize = pageSize.baseFontSize;
	const key = createLocation(pageIndex, atomId);
	const elementData = useRecoilValue(PageElementsFamily(key)).data;
	const inPopOut = useRecoilValue(IsInPopOut);
	const constrainDimensions = useRecoilValue(ConstrainDimensionsSelector);
	const filterColors = useRecoilValue(FilterColors);

	// the portal will be either the .story-background or the containing cell
	const [portal, setPortal] = useState<Element | DocumentFragment>();
	let splits: string[] = useMemo(() => [], []);
	const positionFromContext = useContext(PositionContext);

	const isInSplitPage = useContext(SplitPageContext);
	const isInFlexBox = useContext(IsInFlexBoxContext);

	let position: PositionContextType = positionFromContext;
	if (
		navigationType === "scroll" ||
		navigationType === "scrollCards" ||
		navigationType === "scrollContinuous"
	) {
		if (pageIndex === globalPageIndex) {
			position = "top";
		} else if (pageIndex === globalPageIndex + 1) {
			position = "bottom";
		}
	}

	const portalId = getPortalId(position, navigationType);
	const animationTarget = isInCell ? backGroundRef : portalId;
	useBackgroundAnimation({
		target: animationTarget,
		pageIndex,
		position,
		atomId,
	});

	useLayoutEffect(() => {
		// Early returns for performance optimization
		const isScrollNavigation = navigationType === "scroll";
		const isCurrentOrNextPage =
			pageIndex === globalPageIndex || pageIndex === globalPageIndex + 1;

		if (isScrollNavigation && !isCurrentOrNextPage) {
			return;
		}

		if (!elementData.tapped || !backGroundRef.current) {
			return;
		}

		// Reset states
		setIsInCell(false);
		setPortal(undefined);

		const currentElement = backGroundRef.current;
		const containingCell = currentElement.closest(
			strings.css.styledGridCellClass,
		) as HTMLDivElement;

		if (containingCell && !isInSplitPage) {
			setIsInCell(true);
			setPortal(containingCell);
			return;
		}

		const viewer = currentElement.closest(".viewer-container");
		if (viewer) {
			const container = viewer.querySelector(portalId);
			if (container) {
				setPortal(container);
			}
		}
	}, [
		elementData.tapped,
		isInSplitPage,
		portalId,
		globalPageIndex,
		pageIndex,
		navigationType,
	]);

	if (elementData.content && typeof elementData.content === "string") {
		splits = elementData.content.split(controlCharsObject["|"].controlChar);
	}

	const tapped = elementData.tapped;

	const splitCells = splits.map((split, idx) => (
		<BackgroundGridCell
			atomId={atomId}
			position={position}
			pageIndex={pageIndex}
			content={split}
			tapped={tapped}
			isInGridCell={isInCell}
			modifier={elementData.modifier}
			key={`split-background-${
				// biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
				idx
			}`}
		/>
	));

	if (
		navigationType === "scroll" &&
		pageIndex !== globalPageIndex &&
		pageIndex !== globalPageIndex + 1
	) {
		return null;
	}

	return (
		<StyledBackground
			$background=""
			$baseFontSize={baseFontSize}
			$baseFontSizeRaw={pageSize.baseFontSizeRaw}
			$elementIndex={atomId}
			$filterColors={filterColors}
			$constrainDimensions={constrainDimensions}
			$isInCell={isInCell}
			$isInFlexBox={isInFlexBox}
			$isInPopOut={inPopOut}
			$modifier={elementData.modifier}
			$onePercentHeight={pageSize.onePercentHeight}
			$onePercentWidth={pageSize.onePercentWidth}
			$pageSize={pageSize}
			$isInPortrait={!pageSize.landscape}
			$tapped={tapped}
			aria-hidden={true}
			aria-label={elementData.altText}
			className={`${strings.css.pageElement}  background`}
			ref={backGroundRef}
			role="img"
		>
			{portal && !isInCell && createPortal(splitCells, portal)}
			{isInCell && splitCells}
		</StyledBackground>
	);
};

export default Background;
